<template>
  <section
    ref="root"
    class="listing-map-card"
    :aria-label="label"
    @keydown="onKeydown"
  >
    <!-- Browsing in a grey header at the top of the card, the same grey band
         the old popup had, so it reads as "there are more" instead of
         disappearing into the footer -->
    <div class="listing-map-card__header">
      <div v-if="total > 1" class="listing-map-card__nav">
        <button
          type="button"
          class="listing-map-card__step listing-map-card__step--prev"
          :disabled="index === 0"
          :aria-label="t('listing-map.card.prev')"
          @click="go(-1)"
        >
          <SvgIcon name="chevron" :size="14" :stroke="2" />
        </button>
        <span class="listing-map-card__counter" aria-live="polite">
          {{ t('listing-map.card.counter', { index: index + 1, total }) }}
        </span>
        <button
          type="button"
          class="listing-map-card__step listing-map-card__step--next"
          :disabled="index === total - 1"
          :aria-label="t('listing-map.card.next')"
          @click="go(1)"
        >
          <SvgIcon name="chevron" :size="14" :stroke="2" />
        </button>
      </div>
      <button
        type="button"
        class="listing-map-card__close"
        :aria-label="t('listing-map.popup.close')"
        @click="emit('close')"
      >
        <SvgIcon name="x" :size="18" :stroke="2.5" />
      </button>
    </div>

    <div
      class="listing-map-card__viewport"
      :class="{ 'is-settling': settling }"
      :style="{ '--listing-map-card-drag': `${dragX}px` }"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerCancel"
      @click.capture="onClickCapture"
    >
      <Transition :name="transitionName" @before-leave="onBeforeLeave" @after-enter="direction = 0">
        <slot />
      </Transition>
    </div>

    <div v-if="$slots.cta" class="listing-map-card__footer">
      <slot name="cta" />
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import SvgIcon from './SvgIcon.vue';
import { t } from '../lib/i18n';

// The fixed selection card at the bottom of a map (WHN-2179): a grey header
// with prev/next, counter and close, then the content, then a footer with the
// CTA slot. The parent supplies the content, because the projects map and the
// listing map show different things for one selected marker.
const props = defineProps({
  index: { type: Number, required: true },
  total: { type: Number, required: true },
  label: { type: String, required: true }
});

const emit = defineEmits(['close', 'prev', 'next', 'resize']);

const root = ref(null);

const SWIPE_DISTANCE = 40;
const SWIPE_VELOCITY = 0.4;
const AXIS_LOCK = 8;
const EDGE_RESISTANCE = 0.3;
const SETTLE_MS = 200;
// A finished swipe is followed by a synthetic click on the item, which would
// open the detail panel
const CLICK_GUARD_MS = 400;

const direction = ref(0);
const dragX = ref(0);
const settling = ref(false);
let gesture = null;
let releaseX = 0;
let clickGuardUntil = 0;
let settleTimer = null;

const transitionName = computed(() => {
  if (direction.value > 0) return 'listing-map-card-next';
  if (direction.value < 0) return 'listing-map-card-prev';
  return 'listing-map-card-fade';
});

const canStep = (delta) => (delta < 0 ? props.index > 0 : props.index < props.total - 1);

const go = (delta) => {
  if (!canStep(delta)) return;
  direction.value = delta;
  emit(delta > 0 ? 'next' : 'prev');
};

const settle = () => {
  settling.value = true;
  dragX.value = 0;
  if (settleTimer) window.clearTimeout(settleTimer);
  settleTimer = window.setTimeout(() => {
    settling.value = false;
  }, SETTLE_MS);
};

const onPointerDown = (event) => {
  if (event.pointerType === 'mouse' || props.total < 2) return;
  gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now(), axis: null };
};

const onPointerMove = (event) => {
  if (!gesture || event.pointerId !== gesture.id) return;
  const dx = event.clientX - gesture.x;
  const dy = event.clientY - gesture.y;
  if (!gesture.axis) {
    if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_LOCK) return;
    gesture.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    if (gesture.axis === 'y') {
      gesture = null;
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
  }
  dragX.value = canStep(dx < 0 ? 1 : -1) ? dx : dx * EDGE_RESISTANCE;
};

const onPointerUp = (event) => {
  if (!gesture || event.pointerId !== gesture.id) return;
  const { axis, time } = gesture;
  gesture = null;
  if (axis !== 'x') return;
  clickGuardUntil = performance.now() + CLICK_GUARD_MS;
  const velocity = dragX.value / Math.max(performance.now() - time, 1);
  let delta = 0;
  if (dragX.value < -SWIPE_DISTANCE || velocity < -SWIPE_VELOCITY) delta = 1;
  if (dragX.value > SWIPE_DISTANCE || velocity > SWIPE_VELOCITY) delta = -1;
  if (delta && canStep(delta)) {
    releaseX = dragX.value;
    dragX.value = 0;
    go(delta);
  } else {
    settle();
  }
};

const onPointerCancel = () => {
  if (!gesture) return;
  gesture = null;
  settle();
};

const onClickCapture = (event) => {
  if (performance.now() >= clickGuardUntil) return;
  event.stopPropagation();
  event.preventDefault();
};

// The leaving item starts its slide from where the finger let go, not from
// the resting position
const onBeforeLeave = (el) => {
  el.style.setProperty('--listing-map-card-drag', `${releaseX}px`);
  releaseX = 0;
};

// Arrow keys only while focus is inside the card: on the canvas they pan the
// map, and that must keep working while the card is open.
const onKeydown = (event) => {
  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    go(-1);
  } else if (event.key === 'ArrowRight') {
    event.preventDefault();
    go(1);
  }
};

// Capture phase, so this runs before the detail panel's own Escape handler:
// the parent can still see that the panel is open and leave the card alone.
const onDocumentKeydown = (event) => {
  if (event.key === 'Escape') emit('close');
};

// The map moves its bottom-right controls out from under the card; the height
// is what it needs for that.
let resizeObserver = null;

onMounted(() => {
  document.addEventListener('keydown', onDocumentKeydown, { capture: true });
  resizeObserver = new ResizeObserver(() => {
    if (root.value) emit('resize', root.value.offsetHeight);
  });
  resizeObserver.observe(root.value);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onDocumentKeydown, { capture: true });
  if (resizeObserver) resizeObserver.disconnect();
  if (settleTimer) window.clearTimeout(settleTimer);
});
</script>
